import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Input,
  InputNumber,
  Modal,
  Slider,
  Spin,
  Switch,
  Tooltip,
  message,
  theme,
} from 'antd';
import { PlusOutlined, SaveOutlined, InfoCircleOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { calculateAll, defaultAgent, defaultParams } from '../services/costEngine';
import { scenarioService } from '../services/scenarioService';
import { fetchPricing } from '../services/pricingService';
import { AgentProfileCard } from '../components/AgentProfileCard';
import { CostBreakdown } from '../components/CostBreakdown';
import { CostAgentChat } from '../components/CostAgentChat';
import { ModelPriceReference } from '../components/ModelPriceReference';
import { ScenarioLibraryModal } from '../components/ScenarioLibraryModal';
import { RoadmapTab } from '../components/RoadmapTab';
import { MeasuredBaselineSimulator } from '../components/MeasuredBaselineSimulator';
import { Sidebar } from '@/components/layout/Sidebar';
import type { AgentProfile, PricingPayload, SavedScenario, SimulationParams } from '../types';

/**
 * The (i) icon that lives in the Collapse title. State is lifted to the
 * parent so the explainer panel can render at the children level
 * (block-level, full card width).
 */
function SemanticsIcon({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  return (
    <Tooltip
      title={t('costSimulator.semanticsLabel')}
      color={token.colorBgContainer}
      overlayInnerStyle={{
        color: token.colorTextSecondary, fontSize: 11,
        border: `1px solid ${token.colorBorderSecondary}`,
        padding: '4px 8px', boxShadow: token.boxShadowSecondary,
      }}
    >
      <InfoCircleOutlined
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        style={{
          cursor: 'pointer',
          opacity: open ? 0.9 : 0.5,
          color: open ? token.colorPrimary : 'inherit',
          fontSize: 12,
          marginInlineStart: 6,
          transition: 'opacity 0.15s, color 0.15s',
        }}
      />
    </Tooltip>
  );
}

/**
 * The explainer body — renders multi-paragraph text in a light panel.
 * Block-level, full width, properly wraps. Sits inside the Collapse children.
 */
function SemanticsPanel() {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  // Split paragraphs on blank lines for cleaner spacing than \n raw.
  const paragraphs = (t('costSimulator.semantics') || '').split(/\n\n+/);
  return (
    <div style={{
      marginBlockEnd: 14,
      padding: '12px 14px',
      background: token.colorFillAlter,
      borderInlineStart: `3px solid ${token.colorPrimary}`,
      borderRadius: token.borderRadius,
    }}>
      {paragraphs.map((p, i) => (
        <p key={i} style={{
          margin: i === paragraphs.length - 1 ? 0 : '0 0 8px 0',
          fontSize: 12,
          lineHeight: 1.65,
          color: token.colorTextSecondary,
          fontWeight: 400,
          wordBreak: 'normal',
          overflowWrap: 'break-word',
        }}>
          {p}
        </p>
      ))}
    </div>
  );
}

/**
 * Two synced controls for concurrent-user capacity:
 *   - Percentage of active users (slider)
 *   - Absolute number of concurrent users (slider + input)
 * Both edit the same underlying `concurrentUsersPct` param via reverse math.
 * Changing either updates the other live.
 */
function ConcurrencyControl({
  params, setParams, pricing,
}: {
  params: SimulationParams;
  setParams: (updater: (p: SimulationParams | null) => SimulationParams | null) => void;
  pricing: PricingPayload;
}) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const baseCap = pricing.simulator.infrastructure.compute.concurrent_included_in_base ?? 40;
  const totalActive = params.agents.reduce((s, a) => s + a.activeUsersPerMonth, 0) * params.numSubTenants;

  // Derived absolute concurrent target = active × pct%, floored at base
  const targetFromPct = Math.max(baseCap, Math.round(totalActive * params.concurrentUsersPct / 100));

  // PERCENTAGE SLIDER RANGE — dynamic floor based on active user count.
  // The base instance ALWAYS handles `baseCap` (40) concurrent for free, so
  // any percentage that maps to fewer than `baseCap` users is meaningless
  // (cost is identical). Floor the percentage at whatever maps to baseCap.
  //   200 active → min pct = ceil(40/200*100) = 20%
  //   1000 active → min pct = ceil(40/1000*100) = 4%
  //   30 active → min pct = 100% (already in base, can't go lower meaningfully)
  const minPct = totalActive > 0
    ? Math.max(1, Math.min(100, Math.ceil((baseCap / totalActive) * 100)))
    : 100;
  const maxPct = 150;

  // ABSOLUTE SLIDER RANGE — floor at base capacity (free), up to 1.5× active
  const absMin = baseCap;
  const absMax = Math.max(absMin + 10, Math.round(totalActive * 1.5));

  const setPct = (pct: number) => {
    setParams(p => p && { ...p, concurrentUsersPct: Math.max(1, Math.min(maxPct, pct)) });
  };
  const setAbsolute = (n: number) => {
    if (totalActive <= 0) return;
    // Reverse-compute the percentage that yields this absolute value
    const newPct = Math.max(1, Math.min(maxPct, Math.round((n / totalActive) * 100)));
    setParams(p => p && { ...p, concurrentUsersPct: newPct });
  };

  return (
    <div style={{
      padding: '12px 14px',
      background: token.colorFillAlter,
      borderRadius: token.borderRadius,
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
        {t('costSimulator.scale.concurrentTitle')}
        <Tooltip title={t('costSimulator.scale.concurrentPctTooltip')}>
          <span style={{ cursor: 'help', opacity: 0.6, fontSize: 11 }}>ⓘ</span>
        </Tooltip>
      </div>

      {/* Row 1: percentage */}
      <div>
        <div style={{ fontSize: 11, opacity: 0.65, marginBlockEnd: 4 }}>
          {t('costSimulator.scale.concurrentPctLabel')}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Slider
            min={minPct} max={maxPct} step={1}
            value={Math.max(minPct, params.concurrentUsersPct)}
            onChange={setPct}
            style={{ flex: 1, marginInline: 4 }}
            tooltip={{ formatter: v => `${v}%` }}
          />
          <InputNumber
            min={minPct} max={maxPct} step={1}
            value={Math.max(minPct, params.concurrentUsersPct)}
            onChange={v => { if (typeof v === 'number' && !Number.isNaN(v)) setPct(v); }}
            formatter={(v) => (v === undefined || v === null || String(v) === '') ? '' : `${v}%`}
            parser={s => {
              const cleaned = String(s ?? '').replace(/[^\d]/g, '');
              return (cleaned === '' ? minPct : Number(cleaned)) as any;
            }}
            size="small" style={{ width: 80, flexShrink: 0 }}
          />
        </div>
        {totalActive > 0 && (
          <div style={{ fontSize: 10, opacity: 0.5, marginBlockStart: 4 }}>
            {t('costSimulator.scale.concurrentPctFloorNote', { minPct, baseCap })}
          </div>
        )}
      </div>

      {/* Row 2: absolute number */}
      <div>
        <div style={{ fontSize: 11, opacity: 0.65, marginBlockEnd: 4 }}>
          {t('costSimulator.scale.concurrentAbsLabel')}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Slider
            min={absMin} max={absMax} step={1}
            value={targetFromPct}
            onChange={setAbsolute}
            disabled={totalActive <= 0}
            style={{ flex: 1, marginInline: 4 }}
            tooltip={{ formatter: v => `${v} users` }}
          />
          <InputNumber
            min={absMin} max={absMax} step={1}
            value={targetFromPct}
            onChange={v => { if (typeof v === 'number' && !Number.isNaN(v)) setAbsolute(v); }}
            disabled={totalActive <= 0}
            size="small" style={{ width: 80, flexShrink: 0 }}
          />
        </div>
        {totalActive > 0 && (
          <div style={{ fontSize: 10, opacity: 0.5, marginBlockStart: 4 }}>
            {t('costSimulator.scale.concurrentAbsFloorNote', { absMin })}
          </div>
        )}
      </div>

      {/* Live summary */}
      <div style={{ fontSize: 11, opacity: 0.7, lineHeight: 1.6 }}>
        {totalActive <= 0
          ? t('costSimulator.scale.concurrentAddAgent')
          : t('costSimulator.scale.concurrentSummary', {
              count: targetFromPct,
              pct: params.concurrentUsersPct,
              active: totalActive.toLocaleString(),
              base: baseCap,
            })}
      </div>
    </div>
  );
}

/** Log-scale slider for fine control of large value ranges. */
function LogSliderInline({ min, max, value, onChange }: { min: number; max: number; value: number; onChange: (v: number) => void }) {
  const SCALE = 1000;
  const logMin = Math.log10(Math.max(1, min));
  const logMax = Math.log10(max);
  const valueToPos = (v: number) => Math.round(SCALE * (Math.log10(Math.max(1, v)) - logMin) / (logMax - logMin));
  const posToValue = (p: number) => {
    const v = Math.pow(10, logMin + (p / SCALE) * (logMax - logMin));
    if (v < 10) return Math.round(v);
    if (v < 100) return Math.round(v / 2) * 2;
    return Math.round(v / 10) * 10;
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBlockStart: 4 }}>
      <Slider
        min={0} max={SCALE}
        value={valueToPos(value)}
        onChange={p => onChange(posToValue(p))}
        style={{ flex: 1 }}
        tooltip={{ formatter: p => (p == null ? '' : posToValue(p).toLocaleString()) }}
      />
      <InputNumber
        min={min} max={max}
        value={value}
        onChange={v => { if (typeof v === 'number' && !Number.isNaN(v)) onChange(v); }}
        size="small" style={{ width: 80 }}
        formatter={(v) => (v === undefined || v === null || String(v) === '') ? '' : Number(v).toLocaleString()}
        parser={s => {
          const cleaned = String(s ?? '').replace(/[^\d]/g, '');
          return (cleaned === '' ? min : Number(cleaned)) as any;
        }}
      />
    </div>
  );
}


export function CostSimulatorPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const insightsAvailable = useFeature('modules.insights');
  // Advanced (custom) is super-admin only — slated for removal once the
  // new Simulator covers every use case. Hide the tab from everyone else.
  // Role check kept available for future per-role gating, but no current
  // call site reads it — Advanced tab is hidden for everyone via a literal
  // `false` guard. If you re-add a role gate, uncomment hasRole below.
  // const { hasRole } = useAuth();

  const [pricing, setPricing] = useState<PricingPayload | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [params, setParams] = useState<SimulationParams | null>(null);
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [, setLoadingScenarios] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [semanticsOpen, setSemanticsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // Live snapshot of the Simulator (measured-baseline) tab's current scenario.
  // Wired via `onSnapshotChange` on <MeasuredBaselineSimulator>. The Cost
  // Advisor uses this so the chat is grounded in WHAT THE USER IS LOOKING
  // AT — not in the now-hidden Advanced tab's empty `params`.
  const [simpleSimSnapshot, setSimpleSimSnapshot] = useState<{ params: any; breakdown: any } | null>(null);
  // Default to the measured-baseline simulator — it's the primary flow
  // (pick an agent → everything else auto-loads from real conversation data).
  // The legacy slider-driven simulator stays available under "Advanced" for
  // power users who want to model hypothetical agents or fine-tune every
  // parameter by hand.
  const [activeTab, setActiveTab] = useState<'measured' | 'simulator' | 'roadmap'>('measured');
  /* When a saved scenario is loaded, capture its id + original name so the
     primary Save button UPDATES it in place instead of always creating a new
     row. "Save as new" forces creation regardless. */
  const [loadedScenarioId, setLoadedScenarioId] = useState<string | null>(null);
  const [loadedScenarioName, setLoadedScenarioName] = useState<string>('');

  useEffect(() => {
    // Use the 60s in-memory pricing cache — `force=true` was making
    // every page mount re-fetch from scratch, which the user
    // experienced as "Cost Simulator loads from zero each time."
    // 60s freshness is plenty for a config-edit signal to propagate.
    fetchPricing()
      .then(p => {
        setPricing(p);
        setParams(defaultParams(p));
      })
      .catch(err => {
        console.error('[CostSimulator] Failed to load pricing:', err);
        setPricingError(err?.message ?? t('costSimulator.failedToLoadPricing'));
      });

    setLoadingScenarios(true);
    scenarioService.list()
      .then(setScenarios)
      .catch(() => {})
      .finally(() => setLoadingScenarios(false));
  }, []);

  const breakdown = useMemo(() => {
    if (!params || !pricing) return null;
    return calculateAll(params, pricing);
  }, [params, pricing]);

  if (pricingError) {
    // Render the standard page chrome (Sidebar + header) so the user
    // can navigate away — don't strand them on a tenant-less error
    // screen.
    return (
      <div className={`admin-container${embedded ? ' cost-embedded' : ''}`}>
        {!embedded && <Sidebar />}
        <div className={`main-container${embedded ? ' cost-embedded' : ''}`}>
          <main className="main-content" style={{ padding: 24 }}>
            <Alert type="error" message={t('costSimulator.errors.pricingFailed')} description={pricingError} showIcon />
          </main>
        </div>
      </div>
    );
  }

  if (!pricing || !params || !breakdown) {
    // Loading state — render Sidebar + page header so the page never
    // looks like a separate full-viewport blank screen. The user
    // reported "Cost Simulator looks like an external page" because
    // the previous loading state was a 100vh full-page spinner with
    // no chrome.
    return (
      <div className={`admin-container${embedded ? ' cost-embedded' : ''}`}>
        {!embedded && <Sidebar />}
        <div className={`main-container${embedded ? ' cost-embedded' : ''}`}>
          <main className="main-content">
            <div className="simple-chat-header">
              <h1 dir="auto" className="bot-title">{t('costSimulator.title')}</h1>
              <p className="text-muted">{t('costSimulator.subtitle')}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
              <Spin />
            </div>
          </main>
        </div>
      </div>
    );
  }

  function updateAgent(index: number, updated: AgentProfile) {
    setParams(prev => {
      if (!prev) return prev;
      const agents = [...prev.agents];
      agents[index] = updated;
      return { ...prev, agents };
    });
  }

  function addAgent() {
    setParams(prev => {
      if (!prev) return prev;
      return { ...prev, agents: [...prev.agents, defaultAgent(prev.agents.length, pricing!)] };
    });
  }

  function deleteAgent(index: number) {
    setParams(prev => {
      if (!prev) return prev;
      return { ...prev, agents: prev.agents.filter((_, i) => i !== index) };
    });
  }

  function duplicateAgent(index: number) {
    setParams(prev => {
      if (!prev) return prev;
      const src = prev.agents[index];
      if (!src) return prev;
      const copy: AgentProfile = {
        ...src,
        id: crypto.randomUUID(),
        name: `${src.name} (copy)`,
      };
      const agents = [...prev.agents];
      agents.splice(index + 1, 0, copy);
      return { ...prev, agents };
    });
  }

  function loadScenario(id: string) {
    const s = scenarios.find(x => x.scenario_id === id);
    if (s) {
      setParams(s.parameters);
      setLoadedScenarioId(s.scenario_id);
      setLoadedScenarioName(s.name);
    }
  }

  function newScenario() {
    setParams(defaultParams(pricing!));
    setLoadedScenarioId(null);
    setLoadedScenarioName('');
  }

  async function deleteScenario(id: string) {
    try {
      await scenarioService.remove(id);
      setScenarios(prev => prev.filter(s => s.scenario_id !== id));
      if (loadedScenarioId === id) {
        setLoadedScenarioId(null); setLoadedScenarioName('');
      }
      message.success(t('costSimulator.scenarios.deleted'));
    } catch {
      message.error(t('common.error'));
    }
  }

  /** Save the currently-loaded scenario IN PLACE (update). Called by the
      primary Save button when a scenario is loaded. */
  async function updateLoadedScenario() {
    if (!loadedScenarioId || !params || !breakdown) return;
    setSaving(true);
    try {
      const updated = await scenarioService.update(loadedScenarioId, loadedScenarioName, params, breakdown);
      setScenarios(prev => prev.map(s => s.scenario_id === updated.scenario_id ? updated : s));
      message.success(t('costSimulator.scenarios.updated'));
    } catch {
      message.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  /** Create a brand-new scenario row. Used by the "Save as new" button. */
  async function saveScenario() {
    if (!saveName.trim() || !params || !breakdown) return;
    setSaving(true);
    try {
      const created = await scenarioService.create(
        saveName.trim(), params, breakdown,
        saveDescription.trim() || null,
      );
      setScenarios(prev => [created, ...prev]);
      setLoadedScenarioId(created.scenario_id);
      setLoadedScenarioName(created.name);
      setSaveModalOpen(false);
      setSaveName('');
      setSaveDescription('');
      message.success(t('costSimulator.scenarios.saved'));
    } catch {
      message.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  /** Called by the library modal when a scenario's name/description is edited
      inline — update local cache and edit-mode banner if it's the loaded one. */
  function applyScenarioUpdate(updated: SavedScenario) {
    setScenarios(prev => prev.map(s => s.scenario_id === updated.scenario_id ? updated : s));
    if (loadedScenarioId === updated.scenario_id) setLoadedScenarioName(updated.name);
  }

  const panelStyle = { background: token.colorBgContainer, borderRadius: token.borderRadiusLG };

  const collapseItems = [
    {
      key: 'scale',
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {t('costSimulator.sections.platformScale')}
          <SemanticsIcon open={semanticsOpen} onToggle={() => setSemanticsOpen(o => !o)} />
        </span>
      ),
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {semanticsOpen && <SemanticsPanel />}
          <div>
            <label style={{ fontSize: 12, opacity: 0.65 }}>{t('costSimulator.scale.numSubTenants')}</label>
            <LogSliderInline
              min={1}
              max={500}
              value={params.numSubTenants}
              onChange={v => setParams(p => p && { ...p, numSubTenants: v })}
            />
          </div>
          <ConcurrencyControl params={params} setParams={setParams} pricing={pricing} />
        </div>
      ),
    },
    {
      key: 'agents',
      label: t('costSimulator.sections.agents'),
      children: (
        <div>
          {params.agents.map((agent, i) => (
            <AgentProfileCard
              key={agent.id}
              agent={agent}
              pricing={pricing!}
              onUpdate={updated => updateAgent(i, updated)}
              onDelete={() => deleteAgent(i)}
              onDuplicate={() => duplicateAgent(i)}
              canDelete={true}
            />
          ))}
          <Button icon={<PlusOutlined />} onClick={addAgent} style={{ width: '100%' }}>
            {t('costSimulator.agents.addAgent')}
          </Button>
        </div>
      ),
    },
    {
      key: 'features',
      label: t('costSimulator.sections.platformFeatures'),
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {insightsAvailable && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBlockEnd: 8 }}>
                <Switch size="small" checked={params.insightsEnabled} onChange={v => setParams(p => p && { ...p, insightsEnabled: v })} />
                <label style={{ fontSize: 13 }}>{t('costSimulator.features.insights')}</label>
              </div>
              {params.insightsEnabled && (
                <div style={{ paddingInlineStart: 24 }}>
                  <label style={{ fontSize: 12, opacity: 0.65 }}>{t('costSimulator.features.insightTemplates')}</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBlockStart: 2 }}>
                    <Slider min={1} max={20} value={params.insightTemplatesPerTenant} onChange={v => setParams(p => p && { ...p, insightTemplatesPerTenant: v })} style={{ flex: 1 }} />
                    <InputNumber
                      min={1} max={20} value={params.insightTemplatesPerTenant}
                      onChange={v => { if (typeof v === 'number' && !Number.isNaN(v)) setParams(p => p && { ...p, insightTemplatesPerTenant: v }); }}
                      size="small" style={{ width: 60 }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      ),
    },
  ];

  return (
    <div className={`admin-container${embedded ? ' cost-embedded' : ''}`}>
      {!embedded && <Sidebar />}
      <div className={`main-container${embedded ? ' cost-embedded' : ''}`}>
        <main className="main-content" style={{ minHeight: 0 }}>
          {/* Header — matches Insights/AdminPage pattern */}
          <div className="simple-chat-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexShrink: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 dir="auto" className="bot-title">{t('costSimulator.title')}</h1>
              <p className="text-muted">{t('costSimulator.subtitle')}</p>
            </div>
            {(() => {
              // Advisor is enabled whenever the Simulator tab has at least one
              // agent. The Advanced (custom) tab is hidden so its `params` is
              // always empty — gating on that broke the chat entry-point.
              const advisorReady = !!simpleSimSnapshot?.params?.agents?.length;
              return (
            <Tooltip title={!advisorReady ? t('costSimulator.agentChat.disabledHint') : t('costSimulator.agentChat.open')}>
              <button
                onClick={() => setChatOpen(true)}
                disabled={!advisorReady}
                aria-label={t('costSimulator.agentChat.open')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: !advisorReady ? 'not-allowed' : 'pointer',
                  padding: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: !advisorReady ? 0.35 : 1,
                  transition: 'transform 0.15s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={e => { if (advisorReady) e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {/* Two-star "AI sparkles" icon — same family as the AI-glyph used elsewhere in app */}
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11 3 L12.6 7.4 L17 9 L12.6 10.6 L11 15 L9.4 10.6 L5 9 L9.4 7.4 Z"
                    fill={token.colorPrimary}
                  />
                  <path
                    d="M17.5 14 L18.4 16.6 L21 17.5 L18.4 18.4 L17.5 21 L16.6 18.4 L14 17.5 L16.6 16.6 Z"
                    fill={token.colorPrimary}
                    opacity="0.7"
                  />
                </svg>
              </button>
            </Tooltip>
              );
            })()}
          </div>

          {/* Tab navigation — matches AdminPage / InsightsDashboardPage pattern */}
          <div className="admin-tabs">
            {/* Primary: simulate from real measured agent data.
                Pick an agent → all per-conversation averages load
                automatically from the cost-monitor ledger. */}
            <button
              className={`tab-btn ${activeTab === 'measured' ? 'active' : ''}`}
              onClick={() => setActiveTab('measured')}
              title={t('costSimulator.measuredModeTip')}
            >
              {t('costSimulator.tabs.simulator')}
            </button>
            {/* Advanced (custom) tab is HIDDEN from every user — slated
                for full deletion in the next sweep. The code path stays
                in the codebase for a moment longer so the rip-out is
                a single clean PR. To re-enable temporarily, change the
                guard below to `true`. */}
            {false && (
              <button
                className={`tab-btn ${activeTab === 'simulator' ? 'active' : ''}`}
                onClick={() => setActiveTab('simulator')}
                title={t('costSimulator.manualModeTip')}
              >
                {t('costSimulator.advancedCustom')}
              </button>
            )}
            <button
              className={`tab-btn ${activeTab === 'roadmap' ? 'active' : ''}`}
              onClick={() => setActiveTab('roadmap')}
            >
              {t('costSimulator.tabs.roadmap')}
            </button>
          </div>

          {/* Library + Save row — only shown on the (now hidden) Advanced
              tab. Kept rendered with `false &&` so it's dead-code-ready
              for the same future deletion sweep. */}
          {false && activeTab === 'simulator' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
              padding: '6px 0 10px',
              flexShrink: 0,
            }}>
              <Button
                size="small"
                icon={<FolderOpenOutlined />}
                onClick={() => setLibraryOpen(true)}
                disabled={scenarios.length === 0}
              >
                {t('costSimulator.scenarios.openLibrary')} ({scenarios.length})
              </Button>

              {loadedScenarioId ? (
                <>
                  <Button
                    size="small" type="primary" icon={<SaveOutlined />}
                    loading={saving} onClick={updateLoadedScenario}
                  >
                    {t('costSimulator.scenarios.updateBtn')}
                  </Button>
                  <Button
                    size="small" icon={<PlusOutlined />}
                    onClick={() => { setSaveName(''); setSaveDescription(''); setSaveModalOpen(true); }}
                  >
                    {t('costSimulator.scenarios.saveAsNew')}
                  </Button>
                  <span style={{
                    fontSize: 11, color: token.colorTextSecondary, marginInlineStart: 6,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t('costSimulator.scenarios.editingHint', { name: loadedScenarioName })}
                  </span>
                  <Button size="small" type="text" onClick={newScenario}>
                    {t('costSimulator.scenarios.newScenario')}
                  </Button>
                </>
              ) : (
                <Button
                  size="small" type="primary" icon={<SaveOutlined />}
                  onClick={() => { setSaveName(''); setSaveDescription(''); setSaveModalOpen(true); }}
                >
                  {t('costSimulator.scenarios.saveBtn')}
                </Button>
              )}
            </div>
          )}

          {/* Tab content — matches platform pattern: overflow:auto + minHeight:0
              so the nested scroll panels (Simulator left/right columns) work. */}
          {/* All three tabs are ALWAYS mounted — we toggle their visibility
              with `display: none` instead of conditional render. That way each
              tab keeps its internal React state (selected agents, sliders,
              draft scenarios, roadmap phases…) when the user jumps between
              tabs. The trade-off is a slightly heavier first paint (all three
              components fire their initial-load API calls on mount); the
              benefit is no more "lost my work" when switching tabs. */}
          <div
            className="tab-content active"
            style={{
              overflow: activeTab === 'simulator' ? 'hidden' : 'auto',
              minHeight: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Advanced (Custom) — left inputs + right breakdown.
                Tab is hidden from all users (see button block above); this
                body branch is gated on the same `false` so the panel never
                renders even if activeTab somehow lands on 'simulator'. */}
            <div
              style={{
                display: 'none',
                flex: 1,
                minHeight: 0,
                gap: 0,
              }}
            >
              <div style={{
                width: '42%',
                minWidth: 320,
                overflowY: 'auto',
                paddingInlineEnd: token.paddingMD,
                borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
              }}>
                <Collapse defaultActiveKey={['scale', 'agents', 'features']} items={collapseItems} style={panelStyle} />
              </div>

              <div style={{
                flex: 1,
                overflowY: 'auto',
                paddingInlineStart: token.paddingMD,
              }}>
                {params.agents.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{
                      padding: 40,
                      textAlign: 'center',
                      background: token.colorBgContainer,
                      borderRadius: token.borderRadiusLG,
                      border: `1px dashed ${token.colorBorder}`,
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 600, marginBlockEnd: 8 }}>
                        {t('costSimulator.empty.title')}
                      </div>
                      <div style={{ fontSize: 13, opacity: 0.7 }}>
                        {t('costSimulator.empty.subtitle')}
                      </div>
                    </div>
                    <ModelPriceReference pricing={pricing} />
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <CostBreakdown breakdown={breakdown} />
                    <ModelPriceReference pricing={pricing} />
                  </div>
                )}
              </div>
            </div>

            {/* Simulator (rebuilt simple-sim tab). */}
            <div
              style={{
                display: activeTab === 'measured' ? 'block' : 'none',
                flex: 1,
                overflowY: 'auto',
                padding: '0 4px',
                minHeight: 0,
              }}
            >
              <MeasuredBaselineSimulator pricingPayload={pricing} onSnapshotChange={setSimpleSimSnapshot} />
            </div>

            {/* Roadmap (multi-phase plan). */}
            <div
              style={{
                display: activeTab === 'roadmap' ? 'block' : 'none',
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              <RoadmapTab pricing={pricing} />
            </div>
          </div>
        </main>
      </div>

      <CostAgentChat
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        /* Prefer the live Simulator (simple-sim) snapshot. The Advanced
           `params`/`breakdown` are kept as a safety fallback only — that tab
           is hidden, so this path is reached almost never. */
        params={(simpleSimSnapshot?.params ?? params) as any}
        breakdown={(simpleSimSnapshot?.breakdown ?? breakdown) as any}
        pricing={pricing}
      />

      <ScenarioLibraryModal
        open={libraryOpen}
        pricing={pricing}
        scenarios={scenarios}
        loadedScenarioId={loadedScenarioId}
        onClose={() => setLibraryOpen(false)}
        onLoad={loadScenario}
        onDelete={deleteScenario}
        onScenarioUpdated={applyScenarioUpdate}
      />

      <Modal
        open={saveModalOpen}
        title={t('costSimulator.scenarios.saveModalTitle')}
        onOk={saveScenario}
        onCancel={() => { setSaveModalOpen(false); setSaveName(''); setSaveDescription(''); }}
        confirmLoading={saving}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Input dir="auto"
            autoFocus
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            onPressEnter={saveScenario}
            placeholder={t('costSimulator.scenarios.namePlaceholder')}
          />
          <Input.TextArea dir="auto"
            value={saveDescription}
            onChange={e => setSaveDescription(e.target.value)}
            placeholder={t('costSimulator.scenarios.lib.descriptionPlaceholder')}
            autoSize={{ minRows: 2, maxRows: 5 }}
          />
        </div>
      </Modal>
    </div>
  );
}
